import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { X, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FaTelegram } from 'react-icons/fa';
import GetStartedModal from '../NewHero/GetStartedModal';

const FooterSection = styled.footer`
  padding: 3rem 0;
  font-family: Very Sans, Sans Serif;
  
  @media screen and (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const FooterContainer = styled.div`
  width: 80%;
  margin: 0 auto;
  
  @media screen and (max-width: 768px) {
    width: 90%;
  }
`;

const CTASection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media screen and (max-width: 768px) {
    margin-bottom: 2rem;
    padding-bottom: 2rem;
  }
`;

const CTATitle = styled.h2`
  font-size: 46px;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: white;
  font-family: Very Sans Headline, Sans Serif;
  
  @media screen and (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 1rem;
  }
  
  .gradient-text {
    background: linear-gradient(135deg, #3B82F6 0%, #a78bfa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const CTADescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #94A3B8;
  max-width: 600px;
  margin: 0 auto 2rem;
  font-family: Very Sans, Sans Serif;
  
  @media screen and (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }
`;

const GetStartedButton = styled.button`
  background: #3B82F6;
  border: none;
  color: white;
  padding: 14px 32px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: Very Sans, Sans Serif;
  
  &:hover {
    background: #2563EB;
  }

  @media screen and (max-width: 768px) {
    font-size: 14px;
    padding: 12px 24px;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const BrandSection = styled(FooterColumn)``;

const BrandName = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #3B82F6;
  margin-bottom: 1rem;
  font-family: Very Sans Bold, Sans Serif;

  .highlight {
    background: linear-gradient(135deg, #3B82F6 0%, #a78bfa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const BrandDescription = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #94A3B8;
  margin-bottom: 1.5rem;
  font-family: Very Sans, Sans Serif;
  
  @media screen and (max-width: 768px) {
    line-height: 1.5;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94A3B8;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(59, 130, 246, 0.2);
    border-color: rgba(59, 130, 246, 0.4);
    color: #3B82F6;
  }
`;

const ColumnTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: white;
  margin-bottom: 1.5rem;
  font-family: Very Sans Bold, Sans Serif;
`;

const FooterLink = styled.a`
  font-size: 14px;
  color: #94A3B8;
  text-decoration: none;
  margin-bottom: 1rem;
  transition: color 0.2s ease;
  cursor: pointer;
  font-family: Very Sans, Sans Serif;
  
  &:hover {
    color: #ffffff;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Footer = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleNFTLink = () => {
    navigate("/nftdownload");
  };

  const handleGetStarted = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDashboardLink = () => {
    navigate("/dashboard");
  };

  const handleGooglePlayLink = () => {
    window.open(
      "https://play.google.com/store/apps/details?id=com.HydrixApp",
      "_blank"
    );
  };

  const handleTelegramLink = () => {
    window.open(
      "https://t.me/HydrixApp",
      "_blank"
    );
  };

  const handleMineTelegramLink = () => {
    window.open(
      "https://t.me/HydrixGemsBot",
      "_blank"
    );
  };

  const handleTwitterLink = () => {
    window.open(
      "https://x.com/HydrixApp",
      "_blank"
    );
  };

  const handleDownload = () => {
    window.open(
      "https://play.google.com/store/apps/details?id=com.HydrixApp",
      "_blank"
    );
  };

  const handleOpenTermsAndConditions = () => {
    window.open(
      "https://drive.google.com/file/d/1D3Snt0OJaCnzB-xW3Eaz1YVyOOL8gjJQ/view?usp=drive_link",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleOpenPrivacyPolicy = () => {
    window.open(
      "https://drive.google.com/file/d/1n-9f722ZHgD2vCpS5SF2or1FAgBBIilf/view?usp=drive_link",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleOpenWhitepaper = () => {
    window.open(
      "https://ornate-gecko-119471.netlify.app/",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleOpenAML = () => {
    window.open(
      "https://drive.google.com/file/d/1d4BWfbYhY0YmxKiP9MkJ0qed0K-JNfWP/view?usp=drive_link",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleNodeDownload = useCallback(() => {
    const downloadUrl = 'https://chainfree.info:5010/download/hydrix-node.apk';
    
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'hydrix-node.apk';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('🚀 Hydrix Node APK download initiated');
  }, []);

  return (
    <FooterSection>
      <FooterContainer>
        <CTASection>
          <CTATitle>
            Ready to Start <span className="gradient-text">Mining</span>?
          </CTATitle>
          <CTADescription>
            Join the future of cryptocurrency mining with Hydrix. Start earning from day one with our proven infrastructure.
          </CTADescription>
          <GetStartedButton onClick={handleGetStarted}>
            Start
          </GetStartedButton>
        </CTASection>

        <FooterContent>
          <BrandSection>
            <BrandName><span className="highlight">Hydrix</span></BrandName>
            <BrandDescription>
              Next-generation cryptocurrency mining platform built for scale, security, and fairness.
            </BrandDescription>
            <SocialLinks>
              <SocialLink onClick={handleTwitterLink}>
                <X size={18} />
              </SocialLink>
              <SocialLink onClick={handleTelegramLink}>
                <FaTelegram size={18} />
              </SocialLink>
              <SocialLink>
                <Mail size={18} />
              </SocialLink>
            </SocialLinks>
          </BrandSection>

          <FooterColumn>
            <ColumnTitle>Earn</ColumnTitle>
            <FooterLink onClick={handleDownload}>Android</FooterLink>
            <FooterLink>iOS</FooterLink>
            <FooterLink onClick={handleMineTelegramLink}>Telegram</FooterLink>
            <FooterLink onClick={handleNFTLink}>NFT's</FooterLink>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Resources</ColumnTitle>
            <FooterLink onClick={handleOpenPrivacyPolicy}>Data Protection</FooterLink>
            <FooterLink onClick={handleOpenAML}>Money Laundering</FooterLink>
            <FooterLink onClick={handleOpenTermsAndConditions}>Terms & Conditions</FooterLink>
            <FooterLink onClick={handleOpenWhitepaper}>Whitepaper</FooterLink>
            <FooterLink onClick={handleNodeDownload}>Node Download</FooterLink>
          </FooterColumn>
        </FooterContent>
      </FooterContainer>
      <GetStartedModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
      />
    </FooterSection>
  );
};

export default Footer;